// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonProps } from "@insite/mobius/Button";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import isRelativeUrl from "@insite/mobius/utilities/isRelativeUrl";
import * as React from "react";

interface Props extends ButtonProps {
    target?: string;
    href?: string;
    stopPropagation?: boolean;
}
const ButtonLink: React.FC<Props & HasHistory> = ({
    children,
    href,
    onClick,
    stopPropagation,
    target,
    history,
    disabled,
    ...otherProps
}) => {
    if (!children) {
        return null;
    }
    const onClickIsFunction = typeof onClick === "function";
    const forwardProps: {
        as: "a" | "button";
        href?: string;
        onClick?: React.EventHandler<React.MouseEvent>;
        tabIndex: 0 | -1;
        target?: string;
    } = { tabIndex: 0, as: "a" };
    if (href && !disabled) {
        const isRelativeLink = isRelativeUrl(href);
        forwardProps.href = href;
        forwardProps.target = target;
        forwardProps.onClick = e => {
            stopPropagation && e.stopPropagation();
            onClickIsFunction && onClick!(e as any);
            // cribbed from react-router-dom Link component
            if (
                !e.defaultPrevented && // onClick prevented default
                e.button === 0 && // ignore everything but left clicks
                (!target || target === "_self") && // let browser handle "target=_blank" etc.
                !(e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) // ignore clicks with modifier keys
            ) {
                e.preventDefault();
                if (history && isRelativeLink && !href.startsWith("/RedirectTo/")) {
                    history.push(href);
                } else {
                    window.location.href = href;
                }
            }
        };
    } else {
        forwardProps.as = "button";
    }
    if (disabled) {
        forwardProps.tabIndex = -1;
    }
    return (
        <Button {...forwardProps} {...otherProps} disabled={disabled}>
            {children}
        </Button>
    );
};
ButtonLink.defaultProps = {
    stopPropagation: false,
};
/** @component */
export default withHistory(ButtonLink);
